import React, { useState } from 'react';
import { UserRole, UserType } from '../types/index';
import { Edit3, X, Plus, Activity, Trash2 } from 'lucide-react';
import { usersService } from '../services/dataService';

export default function UsersView({ company, currentUser, users, setAllUsers }) {
  const [editingUser, setEditingUser] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isAdmin = currentUser.role === UserRole.ADMIN;
  const isOwner = currentUser.role === UserRole.OWNER;
  
  const companyUsers = users.filter(u => u.companies.includes(company.id));

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    
    // Evitar submissão dupla
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      const { id, ...updates } = editingUser;
      
      // Verificar se o usuário já existe no Firestore (tem id do Firestore)
      if (id && !id.startsWith('u-')) {
        // É um ID do Firestore, fazer update
        await usersService.update(id, updates);
        // A subscription irá atualizar o estado automaticamente
      } else {
        // É um usuário mock ou novo, criar no Firestore
        await usersService.add(editingUser);
        // A subscription irá atualizar o estado automaticamente
      }
      
      setEditingUser(null);
    } catch (error) {
      console.error("Erro ao atualizar utilizador:", error);
      alert("Erro ao atualizar usuário: " + (error.message || "Erro desconhecido"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    // Evitar submissão dupla
    if (isSaving) return;
    
    const name = e.target.userName.value.trim();
    const email = e.target.userEmail.value.trim();
    const password = e.target.userPass.value;
    const role = e.target.userRole.value;
    const userType = (e.target.userType && e.target.userType.value) || UserType.COMPLEX; // Por padrão COMPLEX

    // Verificar e-mail duplicado
    const existingUser = users.find(u => u.email.toLowerCase() === email.toLowerCase());
    if (existingUser) {
      alert("Já existe um utilizador com este e-mail.");
      return;
    }

    setIsSaving(true);
    try {
      const newUser = {
        name,
        email,
        password,
        role,
        userType, // Adicionar userType
        companies: [company.id]
      };

      // Criar no Firestore - a subscription irá atualizar o estado automaticamente
      await usersService.add(newUser);
      
      setShowAddUser(false);
      
      // Limpar formulário
      e.target.reset();
    } catch (error) {
      console.error("Erro ao adicionar utilizador:", error);
      alert("Erro ao criar usuário: " + (error.message || "Erro desconhecido"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!isAdmin) return; // Only admins can delete
    
    if (userId === currentUser.id) {
      alert("Não pode eliminar o seu próprio utilizador.");
      return;
    }

    if (window.confirm(`Tem certeza que deseja eliminar o membro "${userName}"?`)) {
      try {
        // Verificar se o ID é válido (não é um ID mock)
        if (!userId || userId.startsWith('u-')) {
          alert("Não é possível eliminar este utilizador.");
          return;
        }
        
        await usersService.delete(userId);
        // A subscription irá atualizar o estado automaticamente
        alert("Membro eliminado com sucesso");
      } catch (error) {
        console.error("Erro ao eliminar utilizador:", error);
        alert("Erro ao eliminar membro: " + (error.message || "Erro desconhecido"));
      }
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
           <h1 className="text-4xl font-black text-slate-900 tracking-tight">Equipa Corporativa</h1>
           <p className="text-green-600 font-bold mt-1 uppercase text-[10px] tracking-widest">Controlo de acessos e monitorização</p>
        </div>
        {(isAdmin || isOwner) && (
            <button 
              onClick={() => setShowAddUser(true)}
              className="bg-green-800 text-white px-8 py-4 rounded-2xl font-black flex items-center gap-2 shadow-lg hover:bg-green-900 transition-all active:scale-95"
            >
              <Plus /> Adicionar Membro
            </button>
        )}
      </div>

      <div className="bg-white rounded-[2.5rem] shadow-xl border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[720px]">
          <thead className="bg-[#1b5e20] text-white">
            <tr>
              <th className="p-6 font-black text-[10px] uppercase tracking-[0.2em] opacity-80">Membro</th>
              <th className="p-6 font-black text-[10px] uppercase tracking-[0.2em] opacity-80">Perfil / E-mail</th>
              <th className="p-6 font-black text-[10px] uppercase tracking-[0.2em] opacity-80 text-center">Última Atividade</th>
              <th className="p-6 font-black text-[10px] uppercase tracking-[0.2em] opacity-80 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {companyUsers.map((user, index) => {
              const canEdit = isAdmin || (isOwner && user.role !== UserRole.ADMIN);
              // Usar combinação de id e índice para garantir chaves únicas mesmo se existirem duplicados
              const uniqueKey = user.id ? `${user.id}-${index}` : `user-${index}`;

              return (
                <tr key={uniqueKey} className="hover:bg-green-50/30 transition-colors">
                  <td className="p-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-lg ${user.id === currentUser.id ? 'bg-green-800 text-white shadow-lg' : 'bg-green-100 text-green-900'}`}>
                        {user.name[0]}
                      </div>
                      <span className="font-black text-lg text-slate-900 block">{user.name}</span>
                    </div>
                  </td>
                  <td className="p-6">
                    <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase mb-1 inline-block ${
                      user.role === UserRole.ADMIN ? 'bg-purple-100 text-purple-700' :
                      user.role === UserRole.OWNER ? 'bg-blue-100 text-blue-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {user.role}
                    </span>
                    <p className="text-slate-400 font-bold font-mono text-[11px]">{user.email}</p>
                  </td>
                  <td className="p-6 text-center">
                     <div className="flex flex-col items-center gap-1">
                        <span className="text-[11px] font-black text-slate-900 flex items-center gap-2">
                           <Activity size={12} className={user.lastActive ? 'text-green-500' : 'text-slate-300'}/>
                           {user.lastActive || 'Nunca acedeu'}
                        </span>
                        <div className={`w-2 h-2 rounded-full ${user.lastActive ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)] animate-pulse' : 'bg-slate-200'}`} />
                     </div>
                  </td>
                  <td className="p-6 text-right">
                    <div className="flex items-center justify-end gap-3">
                       {canEdit && <button onClick={() => setEditingUser(user)} className="p-3 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100"><Edit3 size={18} /></button>}
                       {isAdmin && user.id !== currentUser.id && (
                         <button 
                           onClick={() => handleDeleteUser(user.id, user.name)} 
                           className="p-3 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all"
                           title="Eliminar membro"
                         >
                           <Trash2 size={18} />
                         </button>
                       )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          </table>
        </div>
      </div>

      {showAddUser && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[110] flex items-center justify-center p-4">
          <form onSubmit={handleAddUser} className="bg-white rounded-[3rem] p-12 w-full max-w-md shadow-2xl space-y-6">
             <div className="flex justify-between items-center"><h2 className="text-2xl font-black text-slate-900">Novo Membro</h2><button type="button" onClick={() => setShowAddUser(false)}><X size={24}/></button></div>
             <div className="space-y-4">
               <div><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 px-2">Nome Completo</label><input name="userName" required className="w-full p-4 bg-slate-50 rounded-2xl font-bold" /></div>
               <div><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 px-2">E-mail</label><input name="userEmail" type="email" required className="w-full p-4 bg-slate-50 rounded-2xl font-bold" /></div>
               <div><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 px-2">Password</label><input name="userPass" required className="w-full p-4 bg-slate-50 rounded-2xl font-bold" /></div>
               <div><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 px-2">Perfil</label><select name="userRole" className="w-full p-4 bg-slate-50 rounded-2xl font-bold appearance-none">
                  <option value={UserRole.EMPLOYEE}>Funcionário</option>
                  <option value={UserRole.OWNER}>Dono</option>
                  {isAdmin && <option value={UserRole.ADMIN}>Administrador</option>}
               </select></div>
               <div><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 px-2">Tipo de Utilizador</label><select name="userType" className="w-full p-4 bg-slate-50 rounded-2xl font-bold appearance-none" defaultValue={UserType.COMPLEX}>
                  <option value={UserType.COMPLEX}>Complexo</option>
                  <option value={UserType.SIMPLE}>Simples</option>
               </select></div>
             </div>
             <button type="submit" disabled={isSaving} className="w-full py-5 bg-green-800 text-white font-black rounded-2xl text-xl shadow-xl disabled:opacity-50 disabled:cursor-not-allowed">
               {isSaving ? 'A guardar...' : 'Adicionar à Equipa'}
             </button>
          </form>
        </div>
      )}

      {editingUser && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[110] flex items-center justify-center p-4">
          <form onSubmit={handleUpdateUser} className="bg-white rounded-[3rem] p-12 w-full max-w-md shadow-2xl space-y-8">
             <div className="flex justify-between items-center"><h2 className="text-2xl font-black text-slate-900">Editar Membro</h2><button type="button" onClick={() => setEditingUser(null)}><X size={24}/></button></div>
             <div className="space-y-6">
               <div><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 px-2">Nome</label><input required className="w-full p-5 bg-slate-50 rounded-2xl font-bold text-slate-900" value={editingUser.name} onChange={e => setEditingUser({...editingUser, name: e.target.value})} /></div>
               <div><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 px-2">E-mail</label><input type="email" required className="w-full p-5 bg-slate-50 rounded-2xl font-bold text-slate-900" value={editingUser.email} onChange={e => setEditingUser({...editingUser, email: e.target.value})} /></div>
               <div><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 px-2">Nova Password</label><input required className="w-full p-5 bg-slate-50 rounded-2xl font-bold text-slate-900" value={editingUser.password} onChange={e => setEditingUser({...editingUser, password: e.target.value})} /></div>
               <div><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1 px-2">Tipo de Utilizador</label><select className="w-full p-5 bg-slate-50 rounded-2xl font-bold text-slate-900 appearance-none" value={editingUser.userType || UserType.COMPLEX} onChange={e => setEditingUser({...editingUser, userType: e.target.value})}>
                  <option value={UserType.COMPLEX}>Complexo</option>
                  <option value={UserType.SIMPLE}>Simples</option>
               </select></div>
             </div>
             <div className="flex gap-4 pt-4">
               <button type="button" onClick={() => setEditingUser(null)} disabled={isSaving} className="flex-1 py-5 bg-slate-100 text-slate-500 font-bold rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed">Cancelar</button>
               <button type="submit" disabled={isSaving} className="flex-1 py-5 bg-green-800 text-white font-black rounded-2xl shadow-xl disabled:opacity-50 disabled:cursor-not-allowed">
                 {isSaving ? 'A guardar...' : 'Guardar'}
               </button>
             </div>
          </form>
        </div>
      )}
    </div>
  );
}
